import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * An anchored popover that renders through a portal.
 *
 * The portal is the whole point, not an implementation detail. The chat dock's
 * card is `overflow-hidden` (FloatingPanel), so a popover positioned inside it
 * clips at the panel edge — which is why the filter row previously used a
 * native `<select>` instead of a menu. Rendering to `document.body` at a
 * z-index above the panel is the only way to open a real popover from inside
 * the dock.
 *
 * Deliberately not a modal: no focus trap, no scroll lock, no backdrop. It is a
 * transient surface over a panel the user is still working in, matching how
 * FloatingPanel itself reasons about the page behind it.
 */

/** Above FloatingPanel's z-40 and the drawer's z-50. */
const Z_INDEX = 300;

/** Gap between the anchor and the popover, and the minimum viewport inset. */
const GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 10;

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {React.RefObject<HTMLElement>} props.anchorRef - Element to position against.
 * @param {() => void} props.onClose
 * @param {string} props.label - Accessible name.
 * @param {number} [props.width=302] - Popover width in px.
 * @param {'end'|'start'} [props.align='end'] - Which anchor edge to align to.
 * @param {string} [props.className]
 * @param {React.ReactNode} props.children
 */
export function Popover({
  open,
  anchorRef,
  onClose,
  label,
  width = 302,
  align = "end",
  className = "",
  children,
}) {
  const panelRef = useRef(null);
  const [box, setBox] = useState(null);

  const measure = useCallback(() => {
    const anchor = anchorRef?.current;
    if (!open || !anchor) {
      setBox(null);
      return;
    }

    const rect = anchor.getBoundingClientRect();
    // Right-aligned to the anchor by default — the Filters trigger sits at the
    // right of its row, and a left-aligned panel would hang off the panel edge.
    const rawLeft = align === "end" ? rect.right - width : rect.left;
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX;
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(rawLeft, maxLeft));

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN_PX;

    setBox({
      top: rect.bottom + GAP_PX,
      left,
      width,
      maxHeight: Math.max(160, spaceBelow - GAP_PX),
      // How far the caret sits from the popover's own left edge, so it keeps
      // pointing at the trigger even after the viewport clamp moves the panel.
      caretLeft: Math.round(rect.left + rect.width / 2 - left),
    });
  }, [open, anchorRef, width, align]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onReflow = () => measure();
    window.addEventListener("resize", onReflow);
    // Capture phase: the dock and its scroll containers are not ancestors of
    // the portal, so a bubbling listener would never see their scroll.
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      // The anchor is excluded so the trigger's own click can toggle. Without
      // this, closing here and reopening in the trigger's onClick makes the
      // button appear dead.
      if (anchorRef?.current?.contains(event.target)) return;
      onClose?.();
    };

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      // FloatingPanel listens for Escape on the document to close the whole
      // dock. Dismissing a popover must not also close the panel behind it.
      event.stopPropagation();
      onClose?.();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !box) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={label}
      style={{
        position: "fixed",
        top: box.top,
        left: box.left,
        width: box.width,
        maxHeight: box.maxHeight,
        zIndex: Z_INDEX,
      }}
      className={`overflow-visible rounded-[14px] border border-black/5 bg-white shadow-[0_2px_8px_-2px_rgba(0,0,0,0.12),0_18px_44px_-12px_rgba(15,23,42,0.32)] ${className}`}
    >
      {/* Caret sits outside the scrolling body so it is never clipped. */}
      <span
        aria-hidden="true"
        style={{ left: box.caretLeft }}
        className="absolute -top-1.5 h-[11px] w-[11px] -translate-x-1/2 rotate-45 border-l border-t border-black/5 bg-white"
      />
      <div className="relative overflow-y-auto rounded-[14px]">{children}</div>
    </div>,
    document.body
  );
}

export default Popover;
